import { defineComponent as _defineComponent } from 'vue'

import MediaUtil from "@/modules/utils/mediaUtil";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import { ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { PropType, reactive, toRefs, inject } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import MediaController from "@/controllers/communityTalk/mediaController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CustomLabel from "./CustomLabel.vue";

interface State {
	/**
	 * inputHTMLの内容。
	 */
	inputElement: HTMLInputElement | null;
	/**
	 * 画像投稿APIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * 画像投稿APIエラーメッセージ。
	 */
	errorMessage: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 親コンポーネントからのAPIエラーメッセージ。
	 */
	parentErrorMessage: string;
}
// reactiveな変数宣言

export default /*#__PURE__*/_defineComponent({
  props: {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
		default: "",
	},
	/**
	 * 投稿種類。
	 * トークルームとしてのニックネーム: `TalkRoom`
	 * リプライとしてのニックネーム: `Reply`
	 */
	postType: {
		type: String,
		required: true,
		default: "TalkRoom",
		validator(val: string) {
			return ["TalkRoom", "Reply"].includes(val);
		},
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 親コンポーネントからのAPIエラーオブジェクト。
	 */
	apiValidationErrorViewModel: {
		type: Object as PropType<ApiValidationErrorViewModel | null>,
		required: true,
		default: null,
	},
},
  setup(__props, { expose }) {
  expose()

const props = __props
/**
 * 投稿者プロフィール画像投稿コンポーネント。
 * APIへリクエスト送付も行い、Storeへ投稿されたプロフィール画像IDを保持する。
 * バリデーションエラー表示にも対応。
 */

const mediaController = new MediaController();

const state = reactive<State>({
	inputElement: null,
	status: ApiPostLoadStatus.beforeRequest,
	errorMessage: "",
	isProcessing: false,
	parentErrorMessage: "",
});
const { inputElement, status, errorMessage, isProcessing, parentErrorMessage } = toRefs(state);



const name = props.postType === "TalkRoom" ? "ProfilePicId" : "reply.ProfilePicId";

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// バリデーションエラーメッセージの表示(親コンポーネントからの)
if (props.apiValidationErrorViewModel) {
	const targetValidationErrorViewModel = props.apiValidationErrorViewModel.details.filter((e) => e.target === name);
	if (targetValidationErrorViewModel.length > 0) {
		const validationErrorMessages = targetValidationErrorViewModel.map((e) => e.message);
		state.errorMessage = validationErrorMessages.join(", ");
	}
}

// ファイル選択ボタン押下時の処理
const onClickInputFileButton = (): void => {
	if (state.inputElement) {
		state.inputElement.click();
	}
};

// 画像をアップロード
const picture = async (): Promise<void> => {
	// 画像ファイルが存在しなければ処理を空振り
	if (!state.inputElement) return;
	if (!state.inputElement.files) return;

	state.isProcessing = true;
	state.status = ApiPostLoadStatus.loading;

	try {
		const image = state.inputElement.files[0];

		// 画像ファイルのバリデーションチェック
		const validErrorMessages = mediaController.checkProfilePicture(image);
		if (validErrorMessages.length !== 0) {
			state.errorMessage = validErrorMessages.join(",");
			// inputタグ内部のファイルデータを削除
			state.inputElement.value = "";
			return;
		}

		// APIリクエスト
		const response = await mediaController.createProfilePicture(image, communityTalkStore.getTenantId());

		// Storeへプロフィール画像データを保持
		communityTalkStore.setCommunityTalkUserProfilePicViewModel({
			profilePicIdTmp: response.tmpId,
			profilePicUrl: response.url,
		});
		state.errorMessage = "";
	} catch (e) {
		const otherErrorMessage = "画像のアップロードに失敗しました。";
		// エラー内容がundefinedの場合、一律同一エラー表示
		if (!e) {
			state.errorMessage = otherErrorMessage;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((e as any).details.filter((x: any) => x.target === "ImageFile")[0].code === "ValidationErrorDetail") {
			// バリデーションエラーであれば内容を表示
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			state.errorMessage = (e as any).details.filter((x: any) => x.target === "ImageFile")[0].message;
		} else {
			// それ以外のエラーであれば、一律同一エラー表示
			state.errorMessage = otherErrorMessage;
		}
	} finally {
		// 成功しても失敗しても、再度画像を投稿するようにしたいのでAPIステータスは投稿前のステータスへ
		state.status = ApiPostLoadStatus.beforeRequest;
		state.isProcessing = false;
	}
};

// 画像の登録解除
const detachImage = (): void => {
	// inputタグ内部のファイルデータを削除
	if (!state.inputElement) return;
	state.inputElement.value = "";

	// Storeへプロフィール画像データを保持
	communityTalkStore.setCommunityTalkUserProfilePicViewModel({
		profilePicIdTmp: "",
		profilePicUrl: "",
	});
};

const __returned__ = { mediaController, state, inputElement, status, errorMessage, isProcessing, parentErrorMessage, props, name, communityTalkStore, onClickInputFileButton, picture, detachImage, MediaUtil, ApiPostLoadMaster, CustomLabel }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})